import { promises as fs } from "node:fs";
import { join, relative, sep } from "node:path";
import { atomicSwapDir, ensureDir, pathExists, removeRecursive } from "./filesystem-helpers.js";
import { pruneNonRuntimeArtifacts } from "./supabase-pack-downloader.js";

// Path segments never copied into the pack from a local dev source.
const EXCLUDED_SEGMENTS = new Set([".git", "node_modules", ".claude"]);

/**
 * Excluded iff a segment INSIDE `sourceDir` (never the ancestry of `sourceDir`
 * itself) matches. A real-world pack source commonly lives at
 * `<workspace>/.claude/pack` (e.g. mission-workspace.ts pinning the team pack
 * into a mission) — `sourceDir` itself containing `.claude` must not exclude
 * everything, only nested `.git`/`node_modules`/`.claude` dirs found while
 * copying pack content.
 */
function isExcluded(srcPath: string, sourceDir: string): boolean {
  const rel = relative(sourceDir, srcPath);
  if (rel === "") return false; // sourceDir root itself — never excluded
  return rel.split(sep).some((seg) => EXCLUDED_SEGMENTS.has(seg));
}

/**
 * Install a pack into `packDir` from a LOCAL source directory — no network, no
 * Supabase, no token. Mirrors downloadAndExtractPack's stage→prune→swap, but the
 * source is a directory copy instead of a downloaded tarball. Used by
 * `avatar sync --local <path>`.
 */
export async function installPackFromLocalDir(sourceDir: string, packDir: string): Promise<void> {
  if (!(await pathExists(sourceDir))) {
    throw new Error(`--local source không tồn tại: ${sourceDir}`);
  }
  const stat = await fs.stat(sourceDir);
  if (!stat.isDirectory()) {
    throw new Error(`--local source phải là thư mục: ${sourceDir}`);
  }
  // Guard: refuse to wipe the pack with a directory that has no pack content.
  const looksLikePack =
    (await pathExists(join(sourceDir, "skills"))) ||
    (await pathExists(join(sourceDir, "templates")));
  if (!looksLikePack) {
    throw new Error(
      `--local source không giống pack (thiếu skills/ hoặc templates/): ${sourceDir}`,
    );
  }

  const parent = join(packDir, "..");
  await ensureDir(parent);
  const tmpStageDir = `${packDir}.new-${process.pid}`;
  await removeRecursive(tmpStageDir);
  try {
    await fs.cp(sourceDir, tmpStageDir, {
      recursive: true,
      filter: (src) => !isExcluded(src, sourceDir),
    });
    await pruneNonRuntimeArtifacts(tmpStageDir);
    await atomicSwapDir(tmpStageDir, packDir);
  } finally {
    await removeRecursive(tmpStageDir).catch(() => {});
  }
}
